/**
 * schedulePeek.js — a live peek into a project's REAL Schedule branch, not a rebuilt summary.
 *
 * Opened from a time entry's phase name in the Time Review modal. Matt: "instead of mimicking [the
 * outline], change it so the modal is an iframe of the actual schedule branch, like how the Flight
 * Tracker shows a window to the actual branch, not a duplicate." Reuses the exact mechanism
 * FlightPlanTab.jsx's EntityPeekModal already uses (`buildSplitUrl`, `?z=`, `split=1` — splitView.js):
 * the real app, loaded fresh in an iframe, landed on the project's Schedule root via its own zoom
 * deep link. No parallel implementation — this replaces phaseLandscape.js's mimicked view for this
 * one call site only (TimeCardModal's "Manage phases" link still uses the original phaseLandscape.js
 * modal, since that flow leans on write actions — Allow Time Entries — that aren't the point here).
 *
 * openSchedulePeek(projectId, projectName) — projectId is the Airtable Projects record id (time
 * entry's `Project Record UID`), same id `/project?recordId=` expects.
 */
import { buildSplitUrl } from './splitView.js'

export function openSchedulePeek(projectId, projectName) {
  if (!projectId) return
  const existing = document.getElementById('aj-schedule-peek')
  if (existing) existing.parentNode.removeChild(existing)

  const overlay = document.createElement('div')
  overlay.id = 'aj-schedule-peek'
  overlay.style.cssText = 'position:fixed;inset:0;z-index:1000002;background:rgba(7,39,45,0.5);display:flex;align-items:center;justify-content:center;font-family:DM Sans,sans-serif'

  function onKey(e) { if (e.key === 'Escape') close() }
  function close() {
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay)
    document.removeEventListener('keydown', onKey)
  }
  overlay.addEventListener('click', function (e) { if (e.target === overlay) close() })
  document.addEventListener('keydown', onKey)

  const modal = document.createElement('div')
  modal.style.cssText = 'background:var(--aj-bg-page);border-radius:10px;width:1100px;max-width:95vw;height:88vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 8px 32px rgba(7,39,45,0.22)'

  const head = document.createElement('div')
  head.style.cssText = 'background:#07272D;padding:0.9rem 1.1rem;display:flex;align-items:center;justify-content:space-between;flex-shrink:0'
  const htWrap = document.createElement('div')
  const ht = document.createElement('div'); ht.textContent = 'SCHEDULE'
  ht.style.cssText = 'font-family:Montserrat,sans-serif;font-weight:300;font-size:12px;letter-spacing:0.08em;color:#fff'
  const hp = document.createElement('div'); hp.textContent = projectName || ''
  hp.style.cssText = 'font-size:12px;color:rgba(255,255,255,0.55);margin-top:2px'
  htWrap.appendChild(ht); htWrap.appendChild(hp)
  const x = document.createElement('button'); x.textContent = '✕'
  x.style.cssText = 'background:none;border:none;color:rgba(255,255,255,0.6);font-size:18px;cursor:pointer;line-height:1'
  x.addEventListener('click', close)
  head.appendChild(htWrap); head.appendChild(x)

  // key on src (via a fresh element each open — no reuse) forces a clean outline load landed on
  // this project's Schedule root, rather than any previous peek's iframe state.
  const src = buildSplitUrl(window.location.origin, { p: projectId, r: 'schedule' })
  const iframe = document.createElement('iframe')
  iframe.src = src
  iframe.title = 'Schedule'
  iframe.style.cssText = 'flex:1 1 auto;width:100%;border:none;background:var(--aj-bg-page)'

  modal.appendChild(head)
  modal.appendChild(iframe)
  overlay.appendChild(modal)
  document.body.appendChild(overlay)
}
